import { Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import { Button } from "@/components/ui/button";

export function ContactSection() {
  return (
    <section id="contact" className="section-padding bg-secondary/30 relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16 animate-fade-in-up">
          <div className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            Contact Us
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Get In <span className="text-gradient">Touch</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Have questions about our services? We're here to help. Reach out to us anytime.
          </p>
        </div>

        {/* Contact Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto animate-fade-in-up">
          {/* Consolidated Location Block for Each Office */}
          {siteConfig.locations.map((location, locationIndex) => (
            <div
              key={location.id}
              className="route-card p-8 flex flex-col group relative z-10"
              style={{ animationDelay: `${locationIndex * 150}ms` }}
            >
              {/* Title */}
              <h3 className="font-display font-semibold text-xl mb-6">{location.name}</h3>

              {/* Location Block */}
              <div className="flex gap-4 mb-6 pb-6 border-b border-border/30 relative z-20">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-all duration-300 relative z-20">
                  <MapPin className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground font-medium mb-1">Address</p>
                  <p className="text-foreground font-medium mb-2 whitespace-pre-line">{location.address}</p>
                  <a 
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary text-sm hover:text-primary/80 transition-colors"
                  >
                    View on Map <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>

              {/* Phone Block */}
              <div className="flex gap-4 mb-6 pb-6 border-b border-border/30 relative z-20">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-all duration-300 relative z-20">
                  <Phone className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground font-medium mb-2">Phone</p>
                  <a
                    href={`tel:${location.phone.replace(/\s/g, '')}`}
                    className="block text-lg font-bold text-primary hover:text-primary/80 transition-colors mb-3"
                  >
                    {location.phone}
                  </a>
                  <Button asChild size="sm">
                    <a href={`tel:${location.phone.replace(/\s/g, '')}`}>
                      Call Now
                    </a>
                  </Button>
                </div>
              </div>

              {/* Email Block */}
              <div className="flex gap-4 relative z-20">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-all duration-300 relative z-20">
                  <Mail className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground font-medium mb-2">Email</p>
                  <a
                    href={`mailto:${location.email}`}
                    className="block text-foreground font-medium hover:text-primary transition-colors break-all mb-3"
                  >
                    {location.email}
                  </a>
                  <Button asChild size="sm" variant="outline">
                    <a href={`mailto:${location.email}`}>
                      Send Email
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center animate-fade-in-up animation-delay-200">
          <div className="inline-flex flex-col sm:flex-row items-center gap-6 glass rounded-2xl p-8 neon-border">
            <div className="text-center sm:text-left">
              <h4 className="font-display font-semibold text-xl mb-2">Ready to Book Your Journey?</h4>
              <p className="text-muted-foreground">
                {siteConfig.hasApp 
                  ? "Contact us today or book directly through our app"
                  : "Contact us today to book your journey"}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                asChild
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6"
              >
                <a href={`tel:${siteConfig.phone[0].replace(/\s/g, '')}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </a>
              </Button>
              {siteConfig.hasApp && (
                <Button
                  asChild
                  variant="outline"
                  className="border-primary/50 hover:bg-primary/10"
                >
                  <a 
                    href={siteConfig.appStoreUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Download App
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
